/**
 * Cost guard contract shared by the hub, vikod and the web app (burst-guard).
 *
 * The hub answers an over-limit request with a typed error carrying `retryAfterMs`; clients back
 * off with capped exponential delay plus full jitter and never retry an identical failing request
 * sooner than that. Report activity counters ride the sealed machine report (no new stream).
 */
import { z } from 'zod';
/** Token bucket exhausted for this account / connection. HTTP 429. */
export const RATE_LIMITED = 'rate_limited';
/** The account's daily request budget is spent: non-essential traffic is refused until reset. */
export const BUDGET_DEGRADED = 'budget_degraded';
export const RATE_LIMIT_CODES = new Set([RATE_LIMITED, BUDGET_DEGRADED]);
const Ms = z.number().int().nonnegative().max(86_400_000);
export const RateLimitedError = z.object({
    code: z.enum([RATE_LIMITED, BUDGET_DEGRADED]),
    message: z.string().max(200),
    retryAfterMs: Ms,
});
/** `retryAfterMs` of a typed rate-limit error (HTTP body, rpc error or thrown object), else undefined. */
export function rateLimitRetryAfter(e) {
    if (!e || typeof e !== 'object')
        return undefined;
    const o = e;
    if (typeof o.code === 'string' && RATE_LIMIT_CODES.has(o.code)) {
        const ms = Ms.safeParse(o.retryAfterMs);
        return ms.success ? ms.data : 1_000;
    }
    return o.error ? rateLimitRetryAfter(o.error) : undefined;
}
/**
 * Capped exponential backoff with full jitter, floored by the hub's `retryAfterMs`:
 * `max(retryAfter, random(0.5..1) × min(max, base × 2^attempt))`. `attempt` starts at 0.
 */
export function backoffDelay(attempt, o = {}) {
    const base = o.baseMs ?? 1_000;
    const max = o.maxMs ?? 5 * 60_000;
    const exp = Math.min(max, base * 2 ** Math.max(0, Math.min(30, attempt)));
    const jittered = Math.round(exp * (0.5 + 0.5 * (o.random ?? Math.random)()));
    return Math.min(Math.max(max, o.retryAfterMs ?? 0), Math.max(o.retryAfterMs ?? 0, jittered));
}
const Count = z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER);
/**
 * vikod's encrypted-report delivery counters (sealed inside `machine.status`, shown in
 * You › Machines). Piggybacks on status frames the daemon sends anyway; never triggers one.
 */
export const ReportStats = z.strictObject({
    at: Count,
    /** Report puts acknowledged in the trailing minute. */
    putsLastMinute: Count,
    /** Report puts acknowledged in the trailing hour. */
    putsLastHour: Count,
    /** Highest puts in any one minute during the trailing hour. */
    peakPerMinute: Count,
    /** Superseded snapshot frames collapsed before sealing (trailing hour). */
    coalescedLastHour: Count,
    /** Reports still queued locally. */
    queued: Count,
    /** Delivery is backing off after a rate-limit answer until this time (ms epoch). */
    deferredUntil: Count.optional(),
});
//# sourceMappingURL=request-budget.js.map